import { __ } from '../../functions/object.functions';
import { Icon } from '../../types/icon.type';

/**
 * Configuration of a single menu action.
 *
 * Actions can be nested via `children`, which renders the action as a submenu
 * trigger instead of a selectable item.
 */
export interface MenuAction<T = unknown> {
    /** The value emitted through `actionSelected` when the item is activated. */
    value: T;

    /** The visible label of the item. */
    label: string;

    /** Optional leading icon, referenced via the ICON constant. */
    icon?: Icon;

    /** Optional CSS class applied to the item, e.g. 'danger' for destructive actions. */
    class?: string;

    /** Whether the item is disabled. Defaults to false. */
    disabled?: boolean;

    /** Renders a separator above this item. Defaults to false. */
    separatorBefore?: boolean;

    /** Nested actions. When set, the item becomes a submenu trigger and does not emit on activation. */
    children?: MenuAction<T>[];
}

/**
 * Narrows a menu action to one that opens a submenu.
 *
 * @param action - The action to check.
 * @returns {boolean} Returns true if the action has nested children.
 */
export function hasSubmenu<T>(action: MenuAction<T>): action is MenuAction<T> & { children: MenuAction<T>[] } {
    return !__.IsNullOrUndefinedOrEmpty(action.children);
}
